/**
 * Task #1237 — Master FE widget header con popover dropdown (review fix #3).
 * Mostra top-3 tenant per pct DB, totale tenant in hard-block, link al
 * dettaglio cluster. Nessun click forzato di navigazione: l'utente vede
 * subito le metriche senza cambiare pagina.
 */
import { useState, useRef, useEffect } from 'react';
import { HardDrive, ShieldOff, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMasterStorageQuota, formatBytes, type MasterTenantQuotaRow } from '@/hooks/useMasterStorageQuota';

const COLOR_BY_STATUS: Record<string, string> = {
  ok: 'bg-emerald-500',
  info: 'bg-sky-500',
  warning: 'bg-amber-500',
  critical: 'bg-orange-500',
  grace: 'bg-red-500',
  blocked: 'bg-red-700',
};

function topByDbPct(rows: MasterTenantQuotaRow[], n: number): MasterTenantQuotaRow[] {
  return [...rows].sort((a, b) => (b.dbPct || 0) - (a.dbPct || 0)).slice(0, n);
}

export default function StorageQuotaWidget(): JSX.Element | null {
  const { worst, blockedCount, rows } = useMasterStorageQuota();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!worst) return null;

  const top3 = topByDbPct(rows, 3);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted dark:hover:bg-white/10"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Stato cluster storage"
      >
        <HardDrive className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        <div className="hidden md:flex flex-col gap-0.5 w-20">
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-muted-foreground w-6">peg.</span>
            <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded">
              <div
                className={`h-full rounded ${COLOR_BY_STATUS[worst.status] || 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, worst.dbPct)}%` }}
              />
            </div>
          </div>
          {blockedCount > 0 && (
            <div className="text-[9px] text-red-600 dark:text-red-400 font-bold">
              {blockedCount} bloccati
            </div>
          )}
        </div>
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full mt-2 w-96 rounded-lg border border-border dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-50 p-4 text-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-foreground dark:text-gray-100">Cluster storage</span>
            <span className="text-[11px] text-muted-foreground">{rows.length} tenant</span>
          </div>

          {blockedCount > 0 && (
            <div className="mb-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-2 py-1.5 flex items-start gap-2">
              <ShieldOff className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
              <div className="text-[11px] text-red-700 dark:text-red-300">
                <span className="font-semibold">{blockedCount}</span> tenant in hard-block
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground dark:text-muted-foreground/70">
              Top 3 per uso DB
            </div>
            {top3.map((row) => (
              <button
                key={row.tenantId}
                type="button"
                onClick={() => { setOpen(false); navigate(`/storage/tenant/${row.tenantId}`); }}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted/50 dark:hover:bg-gray-800/60"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground dark:text-foreground truncate max-w-[60%]">
                    {row.tenantName}
                  </span>
                  <span className="text-xs font-mono">{(row.dbPct || 0).toFixed(1)}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded">
                  <div
                    className={`h-full rounded ${COLOR_BY_STATUS[row.status] || 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(100, row.dbPct || 0)}%` }}
                  />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {formatBytes(row.dbUsedBytes)} / {formatBytes(row.dbMaxBytes)}
                  {(row.dbHardBlockedSince || row.r2HardBlockedSince) && (
                    <span className="ml-2 text-red-600 font-semibold">• bloccato</span>
                  )}
                </div>
              </button>
            ))}
            {top3.length === 0 && (
              <div className="text-[11px] italic text-muted-foreground">Nessun dato disponibile</div>
            )}
          </div>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/storage'); }}
            className="w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-muted dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-foreground dark:text-foreground"
          >
            Dettaglio cluster <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
